import type { IStayPrice } from "@/src/features/listing/types";
import type { PaymentGateway } from "@/src/shared/features/payment";

export type PaymentTiming = "full" | "part";

interface ICancellationTier {
  hours_before: number;
  percent: number;
}

interface ICancellationSource {
  title?: string | null;
  body?: string | null;
  tiers?: ICancellationTier[] | null;
}

interface IRefundRow {
  boundary: "Before" | "After" | "";
  when: string | null;
  title: string;
  detail: string;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const DHAKA_OFFSET_MS = 6 * HOUR_MS;
const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const NON_REFUNDABLE = "This reservation is non-refundable.";

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

const dayStamp = (day: Date) =>
  Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());

const monthDay = (day: Date) =>
  `${SHORT_MONTHS.at(day.getMonth())} ${day.getDate()}`;

/** The last moment of a refund tier, as the app's `cancellationDeadline` (11 PM Dhaka). */
const cancellationDeadline = (checkIn: Date, hoursBefore: number) =>
  dayStamp(checkIn) -
  DHAKA_OFFSET_MS -
  hoursBefore * HOUR_MS +
  DAY_MS -
  HOUR_MS;

const dhakaMonthDay = (instant: number) => {
  const dhaka = new Date(instant + DHAKA_OFFSET_MS);
  return `${SHORT_MONTHS.at(dhaka.getUTCMonth())} ${dhaka.getUTCDate()}`;
};

const relativeToCheckIn = (hours: number) => {
  if (hours === 0) return "check-in";
  if (hours % 24 === 0)
    return `${plural(hours / 24, "day", "days")} before check-in`;
  return `${plural(hours, "hour", "hours")} before check-in`;
};

const refundRow = (
  isAfterLastCutoff: boolean,
  cutoffHours: number,
  percent: number,
  checkIn: Date | null,
): IRefundRow => ({
  boundary: isAfterLastCutoff ? "After" : "Before",
  when: checkIn
    ? dhakaMonthDay(cancellationDeadline(checkIn, cutoffHours))
    : relativeToCheckIn(cutoffHours),
  title:
    percent >= 100
      ? "Full refund"
      : percent <= 0
        ? "No refund"
        : "Partial refund",
  detail:
    percent <= 0
      ? NON_REFUNDABLE
      : `Get back ${Math.min(100, percent)}% of what you paid.`,
});

/** The app's `CancellationRefundSchedule.rows`: refund tiers still ahead, nearest first. */
const refundRows = (
  tiers: ICancellationTier[],
  checkIn: Date | null,
  now: Date,
): IRefundRow[] => {
  const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
  const positive = sorted
    .map((tier) => tier.hours_before)
    .filter((hours) => hours > 0);
  const lastCutoff = positive.length ? (positive.at(-1) ?? 0) : 0;
  const hasPassed = (cutoff: number) =>
    !!checkIn && cancellationDeadline(checkIn, cutoff) < now.getTime();
  const upcoming = sorted
    .filter(
      (tier) =>
        !hasPassed(tier.hours_before === 0 ? lastCutoff : tier.hours_before),
    )
    .map((tier) =>
      refundRow(
        tier.hours_before === 0 && lastCutoff > 0,
        tier.hours_before === 0 ? lastCutoff : tier.hours_before,
        tier.percent,
        checkIn,
      ),
    );
  if (!upcoming.length && sorted.length)
    return [
      { boundary: "", when: null, title: "No refund", detail: NON_REFUNDABLE },
    ];
  return upcoming;
};

export const confirmPayService = {
  nights(checkIn: Date | null, checkOut: Date | null): number {
    if (!checkIn || !checkOut) return 0;
    return Math.max(
      0,
      Math.round((dayStamp(checkOut) - dayStamp(checkIn)) / DAY_MS),
    );
  },

  /** The stay's totals from `GET /listing/{id}/price`, as the app's `BookingRequestController`. */
  totals(price: IStayPrice | null | undefined, nights: number) {
    if (!price || nights <= 0) return null;
    const perNight = price.average_price || price.price;
    const base = price.before_discount * nights;
    const total = perNight * nights;
    return {
      perNightBeforeDiscount: price.before_discount,
      base,
      total,
      discount: Math.max(0, base - total),
    };
  },

  discountLabel(nights: number): string {
    if (nights >= 28) return "Monthly stay discount";
    if (nights >= 7) return "Weekly stay discount";
    return "Long stay discount";
  },

  discountNote(nights: number): string {
    return `This discount is available because you are staying for ${plural(nights, "day", "days")}.`;
  },

  /** The smallest first payment, or null when the stay must be paid in full. */
  minimumPayable(
    price: IStayPrice | null | undefined,
    bookedMinimum: number | null,
    isBooked: boolean,
  ): number | null {
    if (isBooked) return bookedMinimum;
    return price?.partial_allowed ? (price.minimum_payable ?? null) : null;
  },

  canPayPartially(minimum: number | null, payableTotal: number): boolean {
    return minimum !== null && minimum > 0 && minimum < payableTotal;
  },

  amountDueNow(
    timing: PaymentTiming,
    minimum: number | null,
    payableTotal: number,
  ): number {
    return timing === "part" &&
      minimum !== null &&
      confirmPayService.canPayPartially(minimum, payableTotal)
      ? minimum
      : payableTotal;
  },

  /**
   * The whole balance up to what is due now, as Laravel splits it: a gateway part under
   * `minGatewayAmount` is topped up to it, and those few taka stay in the wallet.
   */
  walletSplit(
    balance: number,
    amountDue: number,
    isUsingWallet: boolean,
    minGatewayAmount: number,
  ) {
    const spendable = Math.max(0, Math.floor(balance));
    let walletAmount =
      isUsingWallet && spendable >= 1
        ? Math.max(0, Math.min(spendable, amountDue))
        : 0;
    const shortfall = minGatewayAmount - (amountDue - walletAmount);
    const before = walletAmount;
    if (amountDue - walletAmount > 0 && shortfall > 0)
      walletAmount = Math.max(0, walletAmount - shortfall);
    const remaining = amountDue - walletAmount;
    return {
      walletAmount,
      remaining,
      heldBack: before - walletAmount,
      isPaidByWalletOnly: walletAmount > 0 && remaining <= 0,
    };
  },

  taka(amount: number): string {
    return `৳${amount}`;
  },

  groupedTaka(amount: number): string {
    return `৳${amount.toLocaleString("en-US")}`;
  },

  walletStatus(balance: number, walletAmount: number): string {
    const total = confirmPayService.groupedTaka(balance);
    if (walletAmount === 0) return `${total} available`;
    if (walletAmount >= balance) return `Using your full balance · ${total}`;
    return `Using ${confirmPayService.groupedTaka(walletAmount)} of ${total}`;
  },

  /** Why the wallet isn't spent in full: the gateway's minimum held `heldBack` taka in it. */
  heldBackNotice(
    gateway: PaymentGateway,
    minGatewayAmount: number,
    walletAmount: number,
    heldBack: number,
  ): string | null {
    if (heldBack <= 0) return null;
    const name = gateway === "ssl" ? "Online payment" : "bKash";
    const taka = confirmPayService.groupedTaka;
    return `${name} needs at least ${taka(minGatewayAmount)} per payment. So we use ${taka(walletAmount)} from your wallet and ${taka(heldBack)} stays there for your next booking.`;
  },

  gatewayPrompt(walletAmount: number, remaining: number): string {
    return walletAmount > 0
      ? `Pay the remaining ${confirmPayService.groupedTaka(remaining)} with`
      : "Pay with";
  },

  confirmLabel(isPriceKnown: boolean, payNow: number): string {
    return isPriceKnown && payNow > 0
      ? `Confirm and pay ${confirmPayService.groupedTaka(payNow)}`
      : "Confirm and pay";
  },

  fullPaymentLabel(payableTotal: number): string {
    return payableTotal > 0
      ? `Pay ${confirmPayService.taka(payableTotal)} now`
      : "Pay in full now";
  },

  /** "Pay ৳3,000 now, rest by Nov 08, 2026." from the price's `last_time_partial_payment`. */
  partialDetail(
    minimum: number | null,
    lastPartialPayment: string | null,
  ): string {
    const payNow =
      minimum === null
        ? "Pay a part now"
        : `Pay ${confirmPayService.taka(minimum)} now`;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(lastPartialPayment ?? "");
    if (!match) return `${payNow}, rest later.`;
    const [, year, month, day] = match;
    return `${payNow}, rest by ${SHORT_MONTHS.at(Number(month) - 1)} ${day}, ${year}.`;
  },

  /** "Nov 11 – 13, 2026", the app's `compactDateRange` plus the check-out year. */
  stayDates(checkIn: Date | null, checkOut: Date | null): string | null {
    if (!checkIn || !checkOut) return null;
    const sameMonth =
      checkIn.getFullYear() === checkOut.getFullYear() &&
      checkIn.getMonth() === checkOut.getMonth();
    const end = sameMonth ? `${checkOut.getDate()}` : monthDay(checkOut);
    return `${monthDay(checkIn)} – ${end}, ${checkOut.getFullYear()}`;
  },

  /** The guests fitted to the listing's maximums, as the app's `_fittedToListing`. */
  fitGuests<T extends { adults: number; children: number; infants: number }>(
    guests: T,
    max: { adults: number; children: number; infants: number },
  ): T {
    const clamp = (value: number, low: number, high: number) =>
      Math.min(Math.max(value, low), high);
    return {
      ...guests,
      adults: clamp(guests.adults, 1, Math.max(1, max.adults)),
      children: clamp(guests.children, 0, max.children),
      infants: clamp(guests.infants, 0, max.infants),
    };
  },

  /** The breakdown sheet's first line: "3 nights · Nov 11 – 14". */
  breakdownNights(
    nights: number,
    checkIn: Date | null,
    checkOut: Date | null,
  ): string {
    const count = plural(nights, "night", "nights");
    const dates = confirmPayService.stayDates(checkIn, checkOut);
    return dates ? `${count} · ${dates.replace(/, \d{4}$/, "")}` : count;
  },

  guestsBreakdown(guests: {
    adults: number;
    children: number;
    infants: number;
  }): string {
    return [
      plural(guests.adults, "adult", "adults"),
      guests.children > 0 ? plural(guests.children, "child", "children") : "",
      guests.infants > 0 ? plural(guests.infants, "infant", "infants") : "",
    ]
      .filter(Boolean)
      .join(", ");
  },

  nightlyLine(nights: number, perNight: number): string {
    return `${plural(nights, "night", "nights")} x ${confirmPayService.taka(perNight)}`;
  },

  payNowLabel(gateway: PaymentGateway): string {
    return gateway === "ssl" ? "You pay now" : "You pay now (bKash)";
  },

  /** The nearest refund rule, as the app's `cancellationSummary`. */
  cancellationSummary(
    cancellation: ICancellationSource | null | undefined,
    checkIn: Date | null,
    now: Date,
  ): { title: string; body: string } {
    const rows = refundRows(cancellation?.tiers ?? [], checkIn, now);
    const nearest = rows.at(0);
    if (!nearest) {
      return {
        title: cancellation?.title || "Cancellation policy",
        body: (cancellation?.body ?? "")
          .split("\n")
          .map((line) => line.trim().replace(/^[-*•]\s*/, ""))
          .filter(Boolean)
          .join(" "),
      };
    }
    if (!nearest.when) return { title: nearest.title, body: nearest.detail };
    const deadline = checkIn
      ? `${nearest.boundary.toLowerCase()} ${nearest.when}`
      : `up to ${nearest.when}`;
    return {
      title:
        nearest.title === "Full refund" ? "Free cancellation" : nearest.title,
      body: `${nearest.title} ${deadline}. ${nearest.detail}`,
    };
  },

  /** Why a payment of `amount` would be refused, or null; the app's `paymentAmountError`. */
  paymentAmountError(
    amount: number,
    booking: { total_payable: number; paid: number },
    minimumPayable: number,
    minGatewayAmount: number,
  ): string | null {
    if (amount < minGatewayAmount) return `Pay minimum ${minGatewayAmount} BDT`;
    if (booking.paid === 0 && amount < minimumPayable)
      return `Pay minimum ${minimumPayable} BDT`;
    const dueAfter = booking.total_payable - (booking.paid + amount);
    if (dueAfter > 0 && dueAfter <= minGatewayAmount)
      return `Due amount can not be less than ${minGatewayAmount} BDT`;
    if (dueAfter < 0) return "Amount can not be more than total payable";
    return null;
  },
};
